import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/AuthProvider';
import type { Permission } from '@/auth/permissions';
import { PAGE_SIZES, usePagePrefs, useTableRegistry } from '@/hooks/usePagePrefs';
import { Menu, MenuItem } from './Overlay';

/** Page scaffolding: header, breadcrumbs, tabs and permission gates. */

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1 text-caption text-text-muted">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1">
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="rounded transition-colors hover:text-text-secondary hover:underline"
                >
                  {item.label}
                </Link>
              ) : (
                <span aria-current={isLast ? 'page' : undefined} className="text-text-secondary">
                  {item.label}
                </span>
              )}
              {!isLast && <ChevronRight aria-hidden className="size-3.5 opacity-60" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  className,
  settings = true,
  customiseActions,
}: {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  meta?: ReactNode;
  className?: string;
  /** Hides the per-screen Customise control, for a header that is not a working screen. */
  settings?: boolean;
  /**
   * Page-specific entries shown at the top of the Customise menu - the dashboard's panel chooser,
   * for one - so a page has a single Customise control rather than its own button beside it.
   */
  customiseActions?: CustomiseAction[];
}) {
  /*
   * The page hero.
   *
   * Every list and workspace opens on this band: a slowly drifting mesh of the brand hues behind
   * the title, rather than a title floating on the page ground. It is the one deliberately
   * decorative surface on a screen - the panels below stay calm so the data keeps the attention -
   * and it is drawn entirely in CSS, so it costs no request and follows the theme.
   */
  return (
    <header className={cn('hero-mesh rounded-2xl px-5 py-5 shadow-sm sm:px-7 sm:py-6', className)}>
      {breadcrumbs && (
        <div className="mb-3">
          <Breadcrumbs items={breadcrumbs} />
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <span aria-hidden className="hero-accent" />
          <h1 className="hero-title text-text-primary">{title}</h1>
          {description && (
            <p className="max-w-3xl text-body text-text-secondary">{description}</p>
          )}
          {meta && <div className="flex flex-wrap items-center gap-2 pt-1.5">{meta}</div>}
        </div>
        {/*
          `min-w-0` rather than `shrink-0`: a header with many actions must wrap them onto another
          line. Unshrinkable, they overflowed the hero, and because the hero clips its overflow the
          browser scrolled it sideways to reveal a focused button - hiding the page title.
        */}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {actions}
          {/*
            Settings live in the header rather than in each page's body, which is what puts them
            on every screen without fifteen pages each wiring up their own copy.
          */}
          {settings && <PageSettingsMenu actions={customiseActions} />}
        </div>
      </div>
    </header>
  );
}

/**
 * Standard page padding and max width, so every route lines up.
 *
 * Also where a route's entrance lives: every page mounts through here, so a navigation rises into
 * place once without each screen having to opt in.
 */
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('enter-page mx-auto w-full max-w-[110rem] space-y-6', className)}>
      {children}
    </div>
  );
}

/**
 * Renders children only when the user holds the permission.
 *
 * A convenience for the interface, not a security control: the server checks the same permission
 * on the request itself. Hiding an action the server would refuse is a kindness; it is not what
 * keeps anyone out.
 */
export function PermissionGate({
  permission,
  anyOf,
  children,
  fallback = null,
}: {
  permission?: Permission;
  anyOf?: Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can, canAny } = useAuth();
  const allowed = permission ? can(permission) : anyOf ? canAny(anyOf) : true;
  return <>{allowed ? children : fallback}</>;
}

/** Horizontal tabs driven by the caller, kept accessible with roving semantics. */
export function Tabs({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: { id: string; label: string; count?: number }[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <div className={cn('border-b border-border-subtle', className)}>
      <div role="tablist" className="-mb-px flex gap-1 overflow-x-auto">
        {tabs.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={selected}
              onClick={() => onChange(tab.id)}
              className={cn(
                'flex shrink-0 items-center gap-2 border-b-2 px-3.5 py-2.5 text-small font-medium transition-colors',
                selected
                  ? 'border-brand text-text-primary'
                  : 'border-transparent text-text-muted hover:border-border-strong hover:text-text-secondary',
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-caption tabular',
                    selected
                      ? 'bg-brand-subtle text-brand-text'
                      : 'bg-surface-sunken text-text-muted',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** A labelled section within a detail page. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-h2 text-text-primary">{title}</h2>
          {description && <p className="mt-0.5 text-small text-text-muted">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * Per-screen customisation.
 *
 * What it offers depends on what is on the screen: the density and row count always apply, and the
 * column toggles appear only when a table has registered its columns. Everything here is saved
 * against this route, so a choice made on the asset register does not silently reshape the audit
 * log.
 */
export interface CustomiseAction {
  label: string;
  description?: string;
  icon?: ReactNode;
  onSelect: () => void;
}

function PageSettingsMenu({ actions = [] }: { actions?: CustomiseAction[] }) {
  const { prefs, setPrefs, reset } = usePagePrefs();
  const registry = useTableRegistry();
  const tableColumns = registry?.registration?.columns ?? [];

  const toggleColumn = (id: string) => {
    const hidden = prefs.hiddenColumns.includes(id)
      ? prefs.hiddenColumns.filter((entry) => entry !== id)
      : [...prefs.hiddenColumns, id];
    setPrefs({ hiddenColumns: hidden });
  };

  return (
    <Menu
      label="Customise this page"
      panelClassName="min-w-64"
      trigger={
        <>
          <SlidersHorizontal />
          <span className="sr-only sm:not-sr-only">Customise</span>
        </>
      }
    >
      {(close) => (
        <div className="space-y-1">
          {actions.length > 0 && (
            <div className="border-b border-[var(--rule)] pb-1">
              {actions.map((action) => (
                <MenuItem
                  key={action.label}
                  icon={action.icon}
                  description={action.description}
                  onClick={() => {
                    close();
                    action.onSelect();
                  }}
                >
                  {action.label}
                </MenuItem>
              ))}
            </div>
          )}
          <p className="px-2.5 pt-2 pb-1 text-label text-text-muted">Density</p>
          <div className="flex gap-1 px-2.5 pb-1">
            {(['comfortable', 'compact'] as const).map((density) => (
              <button
                key={density}
                type="button"
                onClick={() => setPrefs({ density })}
                aria-pressed={prefs.density === density}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 text-caption font-medium capitalize transition-colors',
                  prefs.density === density
                    ? 'border-brand-border bg-brand-subtle text-brand-text'
                    : 'border-border-default text-text-secondary hover:bg-surface-hover',
                )}
              >
                {density}
              </button>
            ))}
          </div>

          <p className="px-2.5 pt-2 pb-1 text-label text-text-muted">Rows per page</p>
          <div className="flex gap-1 px-2.5 pb-1">
            {PAGE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => setPrefs({ pageSize: size })}
                aria-pressed={prefs.pageSize === size}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 text-caption font-medium tabular transition-colors',
                  prefs.pageSize === size
                    ? 'border-brand-border bg-brand-subtle text-brand-text'
                    : 'border-border-default text-text-secondary hover:bg-surface-hover',
                )}
              >
                {size}
              </button>
            ))}
          </div>

          {tableColumns.length > 0 && (
            <>
              <p className="px-2.5 pt-2 pb-1 text-label text-text-muted">Columns</p>
              <div className="max-h-56 overflow-y-auto">
                {tableColumns.map((column) => {
                  const visible = !prefs.hiddenColumns.includes(column.id);
                  return (
                    <button
                      key={column.id}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={visible}
                      onClick={() => toggleColumn(column.id)}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-surface-hover"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'grid size-4 shrink-0 place-items-center rounded border',
                          visible
                            ? 'border-brand bg-brand text-white'
                            : 'border-border-strong bg-surface',
                        )}
                      >
                        {visible && <Check className="size-3" />}
                      </span>
                      <span className="min-w-0 truncate text-small text-text-primary">
                        {column.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="mt-1 border-t border-[var(--rule)] pt-1">
            <button
              type="button"
              onClick={reset}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-small text-text-secondary transition-colors hover:bg-surface-hover"
            >
              <RotateCcw className="size-4 text-text-muted" />
              Reset this page
            </button>
          </div>
        </div>
      )}
    </Menu>
  );
}
